import { MergeTagAutoDetectPlugin } from "./editor/MergeTagAutoDetectPlugin";
import { MergeTagNode } from "./editor/MergeTagNode";
import { MergeTagPlugin } from "./editor/mergeTagPlugin";
import { MergeTagTypeaheadPlugin } from "./editor/MergeTagTypeaheadPlugin";
import { ProductBlockButton } from "./editor/ProductBlockButton";
import { ProductBlockMenu } from "./editor/ProductBlockMenu";
import { ProductBlockNode } from "./editor/ProductBlockNode";
import { ProductBlockPlugin } from "./editor/ProductBlockPlugin";
import { ProductContextNode } from "./editor/ProductContextNode";
import { ProductEngagementPlugin } from "./editor/ProductEngagementPlugin";
import { ProductMirrorPlugin } from "./editor/ProductMirrorPlugin";
import {
  hasProductMergeTagsInAuthoringState,
  TEXT_COMPOSER_DEFAULT_PRODUCT_MODEL,
  transformTextComposerMergeTags,
} from "./mergeTagDefinitions";
import { buildTextProductGridConfig } from "./mergeTagFrameConfig";
import { MergeTagsSection } from "./MergeTagsSection";
import { collectProductConsumers } from "./productContexts";
import { collectTileDataItemRefs } from "./productFieldDtl";
import type { TextComposerExtension } from "../../editor/extensionContract";
import type { ProductGridModelValue } from "@/features/EmailEditor/plugins/productGridConstants";

import { MERGE_TAG_PATTERN } from "@/services/mergeTags";

/**
 * Merge tags don't add authoring state fields — they transform the text
 * content of each channel payload by replacing authoring-time aliases
 * (e.g. `{{ product_name }}`) with the real `tile_data.*` merge tags.
 *
 * Editor-side they contribute the `MergeTagNode` plus three Lexical plugins:
 * a command handler for explicit insertion, an auto-detect plugin that
 * converts plain-text aliases into chips on paste, and a typeahead plugin
 * that surfaces tags as you type.
 */
export const mergeTagExtension: TextComposerExtension = {
  id: "mergeTags",

  contentContribution: (state) => {
    const body = state.bodyText ?? "";
    MERGE_TAG_PATTERN.lastIndex = 0;
    if (!MERGE_TAG_PATTERN.test(body)) return null;
    return { text: true };
  },

  component: MergeTagsSection,

  editor: {
    nodes: [MergeTagNode, ProductBlockNode, ProductContextNode],
    plugins: [
      MergeTagPlugin,
      MergeTagAutoDetectPlugin,
      MergeTagTypeaheadPlugin,
      ProductBlockPlugin,
      ProductBlockMenu,
      ProductEngagementPlugin,
      ProductMirrorPlugin,
    ],
    toolbarItems: [ProductBlockButton],
  },

  frameVariantConfigKeys: ["product_grids"],

  contributeFrameVariantConfig: (state, context) => {
    const suggestionRefs = (
      (state.suggestions ?? []) as Array<{ text: string; url?: string }>
    )
      .filter((s) => s.text.trim().length > 0)
      .flatMap((s) => collectTileDataItemRefs(`${s.text}\n${s.url ?? ""}`));

    if (
      !hasProductMergeTagsInAuthoringState(state) &&
      suggestionRefs.length === 0
    ) {
      return null;
    }
    const defaultModel = (context.productModel ??
      TEXT_COMPOSER_DEFAULT_PRODUCT_MODEL) as ProductGridModelValue;
    const consumers = collectProductConsumers(
      state.bodyText ?? "",
      defaultModel,
    );
    if (consumers.length === 0 && suggestionRefs.length === 0) {
      consumers.push({ model: defaultModel, maxProducts: 1 });
    }
    const need = new Map<string, number>();
    for (const ref of suggestionRefs) {
      need.set(ref.model, Math.max(need.get(ref.model) ?? 0, ref.index + 1));
    }
    for (const [model, required] of need) {
      const covered = consumers
        .filter((entry) => entry.model === model)
        .reduce((sum, entry) => sum + entry.maxProducts, 0);
      if (required > covered) {
        consumers.push({ model, maxProducts: required - covered });
      }
    }
    return {
      product_grids: Object.fromEntries(
        consumers.map((entry, index) => [
          String(index),
          buildTextProductGridConfig(entry.model, entry.maxProducts),
        ]),
      ),
    };
  },

  transformSms: (state, payload, context) => {
    const text = transformTextComposerMergeTags(
      payload.content.body.text,
      context.productModel as ProductGridModelValue,
    );
    if (text === payload.content.body.text) return payload;
    return { content: { body: { type: "TEXT", text } } };
  },

  transformRcs: (state, payload, context) => {
    const body = payload.content.body;
    if (body.type === "TEXT") {
      const text = transformTextComposerMergeTags(
        body.text,
        context.productModel as ProductGridModelValue,
      );
      if (text === body.text) return payload;
      return { content: { ...payload.content, body: { type: "TEXT", text } } };
    }

    if (body.type === "IMAGE" && "text" in body && body.text) {
      const text = transformTextComposerMergeTags(
        body.text,
        context.productModel as ProductGridModelValue,
      );
      if (text === body.text) return payload;
      return { content: { ...payload.content, body: { ...body, text } } };
    }

    return payload;
  },

  transformMms: (state, payload, context) => {
    const body = payload.content.body;
    if (body.type === "TEXT") {
      const text = transformTextComposerMergeTags(
        body.text,
        context.productModel as ProductGridModelValue,
      );
      if (text === body.text) return payload;
      return { content: { body: { type: "TEXT", text } } };
    }

    if (body.type === "IMAGE" && "text" in body && body.text) {
      const text = transformTextComposerMergeTags(
        body.text,
        context.productModel as ProductGridModelValue,
      );
      if (text === body.text) return payload;
      return { content: { body: { ...body, text } } };
    }

    return payload;
  },
};
